import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Route, Routes, useNavigate, useParams } from "react-router";
import {
  PICTOS,
  PICTO_NOTE,
  TRAINING_DISCLAIMER,
  TRAINING_ELEARNING_NOTE,
  TRAINING_LAW,
  type AnswerResult,
  type MeResponse,
  type PublicCourse,
  type PublicSection,
  type Signature,
  type TrainingAnswers,
  type TrainingAttempt,
  type TrainingCourseInfo,
  type TrainingListResponse,
  type TrainingOverview,
  type TrainingSettings,
  type TrainingSlide,
  type TrainingTeamRow,
} from "@handvero/shared";
import { api, errorText } from "../api";
import { DHead } from "./Desktop";
import { SignatureSheet } from "./Protocols";
import { Picto, pictoNote } from "./Pictograms";
import { SetupSheet } from "./TrainingSetup";
import { VeroStage } from "./VeroScene";
import { Icon, Message, PdfViewer, Sheet, useUnauthorizedRedirect } from "./ui";
import "./trainings.css";

/*
 * Online-Schulungen in der Windows-App. Übersicht mit Kursen, Team-Nachweisen und dem, was das Gesetz verlangt;
 * daneben der Schulungsraum: Kapitel-Film, Fragen, bei Fehlern zurück zum Film, am Ende Unterschrift und PDF.
 */

const BASE = "/desktop/schulungen";

function day(iso: string | null): string {
  if (!iso) return "–";
  const d = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T12:00:00`) : new Date(iso);
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

const certUrl = (id: string) => `/api/training-attempts/${id}/certificate`;

export function TrainingsPage({ me }: { me: MeResponse }) {
  return (
    <Routes>
      <Route index element={<TrainingOverviewPage me={me} />} />
      <Route path="teilnahme/:attemptId" element={<TrainingRoom me={me} />} />
    </Routes>
  );
}

/* ---------- Übersicht ---------- */

const TEAM_LABEL: Record<TrainingTeamRow["status"], string> = { passed: "Gültig", due: "Fällig", running: "Läuft", open: "Offen" };

function TrainingOverviewPage({ me }: { me: MeResponse }) {
  const onAuth = useUnauthorizedRedirect();
  const navigate = useNavigate();
  const [data, setData] = useState<TrainingListResponse | null>(null);
  const [team, setTeam] = useState<TrainingOverview | null>(null);
  const [error, setError] = useState("");
  const [setup, setSetup] = useState<TrainingCourseInfo | null>(null);
  const [guestFor, setGuestFor] = useState<string | null>(null);
  const [pdf, setPdf] = useState<{ id: string; title: string } | null>(null);
  const [law, setLaw] = useState(false);

  const load = useCallback(() => {
    api
      .get<TrainingListResponse>("/api/trainings")
      .then((d) => {
        setData(d);
        if (d.canManage) api.get<TrainingOverview>("/api/trainings/overview").then(setTeam, () => undefined);
      })
      .catch((err) => onAuth(err) || setError(errorText(err)));
  }, [onAuth]);
  useEffect(load, [load]);

  async function start(courseId: string, participantName?: string) {
    try {
      const a = await api.post<TrainingAttempt>(`/api/trainings/${courseId}/attempts`, participantName ? { participantName } : {});
      navigate(`${BASE}/teilnahme/${a.id}`);
    } catch (err) {
      setError(errorText(err));
    }
  }

  const courseTitle = (id: string) => data?.courses.find((c) => c.course.id === id)?.course.title ?? "Schulung";
  const due = team?.team.filter((r) => r.status === "due" || r.status === "open").length ?? 0;

  return (
    <section className="dproject dsimple trn">
      <DHead
        kicker="Unterweisungen am Bildschirm"
        title="Schulungen"
        sub="Kurze Filme mit Vero, Fragen zum Verständnis und am Ende ein Nachweis mit Unterschrift. Wer etwas falsch beantwortet, sieht den Abschnitt noch einmal."
        actions={
          <button className="dbtn ghost trn-lawbtn" onClick={() => setLaw(true)}>
            <Icon name="shield" /> Was das Gesetz verlangt
          </button>
        }
      />
      <Message>{error}</Message>
      {!data && !error && <p className="muted-s">Wird geladen …</p>}

      {data?.courses.map((info, i) => {
        const { course, mine, settings } = info;
        const valid = mine?.status === "passed" && mine.validUntil && mine.validUntil >= new Date().toISOString().slice(0, 10);
        const doneCount = mine?.status === "running" ? mine.sectionIds.filter((id) => mine.progress[id]?.passed).length : 0;
        return (
          <article className="trn-course" key={course.id} style={{ animationDelay: `${i * 80}ms` }}>
            <div className="trn-course-art" aria-hidden="true">
              <span className="trn-course-num">{String(i + 1).padStart(2, "0")}</span>
              <ol>
                {course.sections.map((s, k) => (
                  <li key={s.id} className={mine?.progress[s.id]?.passed || valid ? "on" : undefined} style={{ animationDelay: `${200 + k * 60}ms` }}>
                    {s.title}
                  </li>
                ))}
              </ol>
            </div>
            <div className="trn-course-body">
              <p className="dkicker">{course.kicker}</p>
              <h2>{course.title}</h2>
              <p>{course.description}</p>
              <p className="trn-meta">
                <span>{course.sections.length} Abschnitte</span>
                <span>etwa {course.minutes} Minuten</span>
                <span>{settings.videos.length ? `${settings.videos.length} eigene Videos` : "Filme mit Vero"}</span>
              </p>
              {!info.plan.required && (
                <p className="trn-hint">Diese Schulung ist für Sie nicht vorgeschrieben. Sie können sie trotzdem machen.</p>
              )}
              <div className={`trn-state${valid ? " ok" : mine?.status === "running" ? " run" : ""}`}>
                {valid && mine ? (
                  <>
                    <Icon name="check" /> Bestanden am {day(mine.passedAt)} · gültig bis {day(mine.validUntil)}
                  </>
                ) : mine?.status === "running" ? (
                  <>
                    Angefangen · {doneCount} von {mine?.sectionIds.length ?? course.sections.length} Abschnitten geschafft
                  </>
                ) : mine?.status === "passed" ? (
                  <>Ihre letzte Teilnahme ist abgelaufen. Bitte wiederholen Sie die Schulung.</>
                ) : (
                  <>Noch nicht absolviert</>
                )}
              </div>
              <div className="trn-actions">
                <button className="dbtn" onClick={() => void start(course.id)}>
                  {mine?.status === "running" ? "Fortsetzen" : valid ? "Erneut absolvieren" : "Schulung starten"} <Icon name="right" />
                </button>
                {mine?.status === "passed" && (
                  <button className="dbtn ghost" onClick={() => setPdf({ id: mine.id, title: `Nachweis ${me.user.name}` })}>
                    <Icon name="file" /> Mein Nachweis
                  </button>
                )}
                {data.canManage && (
                  <>
                    <button className="dbtn ghost" onClick={() => setSetup(info)}>
                      <Icon name="settings" /> Einrichten
                    </button>
                    <button className="dbtn ghost" onClick={() => setGuestFor(course.id)}>
                      <Icon name="user" /> Für eine Person ohne Konto
                    </button>
                  </>
                )}
              </div>
              {data.canManage && !settings.instructor && (
                <p className="trn-hint">
                  Tipp: Unter „Einrichten“ wählen Sie, welche Module bei Ihnen gelten, ergänzen eigene Laufwege und Fragen und legen fest, wer die Schulung machen muss.
                </p>
              )}
            </div>
          </article>
        );
      })}

      {team && (
        <section className="trn-team">
          <div className="trn-team-head">
            <h2>Nachweise im Team</h2>
            <span className={`trn-badge${due ? " warn" : ""}`}>{due ? `${due} offen oder fällig` : "Alle aktuell"}</span>
          </div>
          <div className="trn-table" role="table" aria-label="Schulungsstand im Team">
            <div className="trn-tr head" role="row">
              <span role="columnheader">Person</span>
              <span role="columnheader">Schulung</span>
              <span role="columnheader">Stand</span>
              <span role="columnheader">Zuletzt bestanden</span>
              <span role="columnheader">Gültig bis</span>
              <span role="columnheader" aria-label="Nachweis" />
            </div>
            {team.team.map((r) => (
              <div className="trn-tr" role="row" key={`${r.userId ?? r.name}-${r.courseId}`}>
                <span role="cell">
                  <b>{r.name}</b>
                  {!r.userId && <small>ohne Konto</small>}
                </span>
                <span role="cell">{courseTitle(r.courseId)}</span>
                <span role="cell">
                  <i className={`trn-dot ${r.status}`} /> {TEAM_LABEL[r.status]}
                </span>
                <span role="cell">{day(r.lastPassedAt)}</span>
                <span role="cell">{day(r.validUntil)}</span>
                <span role="cell">
                  {r.lastPassedAt && r.attemptId && (
                    <button className="btn-text" onClick={() => setPdf({ id: r.attemptId!, title: `Nachweis ${r.name}` })}>
                      PDF ansehen
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
          {team.certificates.length > 0 && (
            <details className="trn-archive">
              <summary>Alle Nachweise ({team.certificates.length})</summary>
              <ul>
                {team.certificates.map((c) => (
                  <li key={c.id}>
                    <span>
                      <b>{c.participantName}</b> · {c.courseTitle}
                    </span>
                    <span className="muted-s">
                      {day(c.passedAt)} · gültig bis {day(c.validUntil)}
                    </span>
                    <button className="btn-text" onClick={() => setPdf({ id: c.id, title: `Nachweis ${c.participantName}` })}>
                      Ansehen
                    </button>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </section>
      )}

      <p className="prot-legal">{TRAINING_DISCLAIMER}</p>

      {law && <LawSheet onClose={() => setLaw(false)} />}
      {setup && (
        <SetupSheet
          info={setup}
          onClose={() => setSetup(null)}
          onSaved={() => {
            setSetup(null);
            load();
          }}
        />
      )}
      {guestFor && (
        <GuestSheet
          onClose={() => setGuestFor(null)}
          onStart={(name) => {
            const id = guestFor;
            setGuestFor(null);
            void start(id, name);
          }}
        />
      )}
      {pdf && <PdfViewer url={certUrl(pdf.id)} title={pdf.title} onClose={() => setPdf(null)} />}
    </section>
  );
}

function LawSheet({ onClose }: { onClose: () => void }) {
  return (
    <Sheet title="Was das Gesetz verlangt" onClose={onClose}>
      <div className="trn-law">
        <p className="muted-s">Eine Zusammenfassung in einfachen Worten, Stand Oktober 2026. Sie ersetzt keine Rechtsberatung.</p>
        {TRAINING_LAW.map((l, i) => (
          <section key={l.title}>
            <h3>
              <span>{String(i + 1).padStart(2, "0")}</span> {l.title}
            </h3>
            <p>{l.text}</p>
            {l.handvero && (
              <p className="trn-law-hv">
                <b>In HandVero:</b> {l.handvero}
              </p>
            )}
            <small>{l.source}</small>
          </section>
        ))}
      </div>
    </Sheet>
  );
}

function GuestSheet({ onClose, onStart }: { onClose: () => void; onStart: (name: string) => void }) {
  const [name, setName] = useState("");
  return (
    <Sheet title="Für eine Person ohne Konto" onClose={onClose}>
      <form
        className="app-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim().length >= 2) onStart(name.trim());
        }}
      >
        <p className="muted-s">Zum Beispiel für eine Aushilfe oder einen neuen Kollegen, der noch kein Konto hat. Die Person macht die Schulung an diesem PC und unterschreibt am Ende selbst.</p>
        <label>
          Vor- und Nachname
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoFocus autoComplete="off" />
        </label>
        <button className="app-btn" disabled={name.trim().length < 2}>
          Schulung starten
        </button>
      </form>
    </Sheet>
  );
}

/* ---------- Schulungsraum ---------- */

type Phase = { kind: "film" } | { kind: "quiz" } | { kind: "result"; result: AnswerResult } | { kind: "final" } | { kind: "done" };

function TrainingRoom({ me }: { me: MeResponse }) {
  const { attemptId = "" } = useParams();
  const onAuth = useUnauthorizedRedirect();
  const [attempt, setAttempt] = useState<TrainingAttempt | null>(null);
  const [info, setInfo] = useState<{ course: PublicCourse; settings: TrainingSettings } | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: "film" });
  const [error, setError] = useState("");
  const [pdf, setPdf] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api
      .get<TrainingAttempt>(`/api/training-attempts/${attemptId}`)
      .then(async (a) => {
        setAttempt(a);
        setInfo(await api.get<{ course: PublicCourse; settings: TrainingSettings }>(`/api/training-attempts/${a.id}/course`));
      })
      .catch((err) => onAuth(err) || setError(errorText(err)));
  }, [attemptId, onAuth]);

  const sections = info?.course.sections ?? [];
  const current = attempt ? sections.find((s) => !attempt.progress[s.id]?.passed) : undefined;
  const index = current ? sections.indexOf(current) : sections.length;

  // Phase aus dem gespeicherten Stand ableiten (z. B. nach dem Neuladen).
  useEffect(() => {
    if (!attempt || !info) return;
    if (attempt.status === "passed") setPhase({ kind: "done" });
    else if (!current) setPhase((p) => (p.kind === "result" ? p : { kind: "final" }));
    else setPhase((p) => (p.kind === "result" ? p : attempt.progress[current.id]?.watched ? { kind: "quiz" } : { kind: "film" }));
  }, [attempt, info, current]);

  useEffect(() => {
    stageRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [phase.kind, index]);

  async function watched() {
    if (!current) return;
    try {
      setAttempt(await api.post<TrainingAttempt>(`/api/training-attempts/${attemptId}/sections/${current.id}/watched`));
      setPhase({ kind: "quiz" });
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function answer(answers: TrainingAnswers) {
    if (!current) return;
    try {
      const result = await api.post<AnswerResult>(`/api/training-attempts/${attemptId}/sections/${current.id}/answers`, { answers });
      setPhase({ kind: "result", result });
    } catch (err) {
      setError(errorText(err));
    }
  }

  function next() {
    if (phase.kind !== "result") return;
    setAttempt(phase.result.attempt);
    setPhase({ kind: "film" });
  }

  if (error && !attempt) {
    return (
      <section className="dproject dsimple">
        <Message>{error}</Message>
        <Link to={BASE} className="btn-text">
          Zurück zu den Schulungen
        </Link>
      </section>
    );
  }
  if (!attempt || !info) return <section className="dproject dsimple" />;

  const { settings } = info;
  const video = current ? settings.videos.find((v) => v.sectionId === current.id) : undefined;
  const note = current ? (settings.notes[current.id] ?? "").trim() : "";
  const passedCount = sections.filter((s) => attempt.progress[s.id]?.passed).length;

  return (
    <section className="dproject dsimple trn-room">
      <div className="trn-room-top">
        <Link to={BASE} className="btn-text prot-back">
          <Icon name="back" /> Schulungen
        </Link>
        <span className="trn-who">
          Teilnehmer/in: <b>{attempt.participantName}</b>
        </span>
      </div>
      <div className="trn-progress" aria-label={`${passedCount} von ${sections.length} Abschnitten geschafft`}>
        <span style={{ transform: `scaleX(${sections.length ? passedCount / sections.length : 0})` }} />
      </div>
      <div className="trn-room-grid">
        <nav className="trn-chapters" aria-label="Abschnitte">
          <p className="dkicker">{info.course.title}</p>
          <ol>
            {sections.map((s, i) => {
              const p = attempt.progress[s.id];
              const state = p?.passed ? "done" : i === index ? "now" : "later";
              return (
                <li key={s.id} className={state}>
                  <span className="trn-ch-num">{p?.passed ? <Icon name="check" /> : String(i + 1).padStart(2, "0")}</span>
                  <span>
                    <b>{s.title}</b>
                    <small>{p?.passed ? (p.tries > 1 ? `bestanden nach ${p.tries} Versuchen` : "bestanden") : i === index ? "jetzt" : "danach"}</small>
                  </span>
                </li>
              );
            })}
            <li className={attempt.status === "passed" ? "done" : index === sections.length ? "now" : "later"}>
              <span className="trn-ch-num">{attempt.status === "passed" ? <Icon name="check" /> : <Icon name="pen" />}</span>
              <span>
                <b>Bestätigen und Nachweis</b>
                <small>Unterschrift, PDF</small>
              </span>
            </li>
          </ol>
          <div className="trn-ask">
            <b>Fragen?</b>
            {settings.instructor ? (
              <p>
                Wenden Sie sich an {settings.instructor}
                {settings.contact ? `, ${settings.contact}` : ""}.
              </p>
            ) : (
              <p>Wenden Sie sich an Ihren Vorgesetzten. Fragen zu stellen gehört zur Unterweisung dazu.</p>
            )}
          </div>
        </nav>

        <div className="trn-stage" ref={stageRef}>
          <Message>{error}</Message>
          {current && phase.kind === "film" && (
            <>
              <StageHead section={current} index={index} step="Film" />
              {video ? (
                <GuardedVideo
                  key={`${current.id}-${attempt.progress[current.id]?.tries ?? 0}`}
                  src={`/api/trainings/${info.course.id}/sections/${current.id}/video?v=${video.version}`}
                  onEnded={() => void watched()}
                />
              ) : (
                <SlideFilm key={`${current.id}-${attempt.progress[current.id]?.tries ?? 0}`} section={current} index={index} onDone={() => void watched()} />
              )}
              {current.slides.some((sl) => sl.pictos?.length) && <p className="trn-nomedia">{pictoNote(current.slides.flatMap((sl) => sl.pictos ?? []), PICTO_NOTE)}</p>}
              {current.sources?.length ? <p className="trn-nomedia">Quellen: {current.sources.join("; ")}.</p> : null}
              {note && <CompanyNote text={note} />}
              {(attempt.progress[current.id]?.tries ?? 0) > 0 && (
                <p className="trn-again">Zweiter Anlauf: Sehen Sie sich den Abschnitt in Ruhe noch einmal an, danach kommen die Fragen erneut.</p>
              )}
            </>
          )}
          {current && phase.kind === "quiz" && (
            <>
              <StageHead section={current} index={index} step="Fragen" />
              <Quiz key={`${current.id}-${attempt.progress[current.id]?.tries ?? 0}`} section={current} seed={attempt.progress[current.id]?.tries ?? 0} onSubmit={answer} />
            </>
          )}
          {current && phase.kind === "result" && (
            <ResultPanel
              result={phase.result}
              section={current!}
              last={!sections.some((s) => !phase.result.attempt.progress[s.id]?.passed)}
              onNext={next}
            />
          )}
          {phase.kind === "final" && (
            <FinalStep
              attempt={attempt}
              confirmText={info.course.confirm}
              suggestedName={attempt.participantName || me.user.name}
              onDone={(a) => {
                setAttempt(a);
                setPhase({ kind: "done" });
              }}
            />
          )}
          {phase.kind === "done" && (
            <div className="trn-done">
              <p className="dkicker">Geschafft</p>
              <h2>Bestanden.</h2>
              <p>
                {attempt.participantName} hat die Schulung „{info.course.title}“ am {day(attempt.passedAt)} abgeschlossen. Der Nachweis gilt bis {day(attempt.validUntil)}.
              </p>
              <div className="trn-actions">
                <button className="dbtn" onClick={() => setPdf(true)}>
                  <Icon name="file" /> Nachweis ansehen
                </button>
                <a className="dbtn ghost" href={`${certUrl(attempt.id)}?download=1`} download>
                  <Icon name="download" /> Als PDF speichern
                </a>
                <Link className="dbtn ghost" to={BASE}>
                  Zur Übersicht
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
      {pdf && <PdfViewer url={certUrl(attempt.id)} title={`Nachweis ${attempt.participantName}`} onClose={() => setPdf(false)} />}
    </section>
  );
}

function StageHead({ section, index, step }: { section: PublicSection; index: number; step: string }) {
  return (
    <header className="trn-stage-head">
      <p className="dkicker">
        Abschnitt {String(index + 1).padStart(2, "0")} · {step}
      </p>
      <h2>{section.title}</h2>
    </header>
  );
}

function CompanyNote({ text }: { text: string }) {
  return (
    <aside className="trn-note">
      <b>Bei uns im Betrieb</b>
      <p>{text}</p>
    </aside>
  );
}

/** Lesezeit je Folie: mindestens 7 Sekunden, bei langen Texten mehr. */
const slideMs = (s: TrainingSlide) => Math.max(7000, (s.title.split(/\s+/).length + s.text.split(/\s+/).length) * 330);

/**
 * Eingebauter Kapitel-Film: Folien laufen von selbst weiter, wie in einem Video. Vorspulen geht nicht,
 * zurück und anhalten schon. Ersetzt sich durch das eigene Video der Firma, sobald eines hochgeladen ist.
 */
function SlideFilm({ section, index, onDone }: { section: PublicSection; index: number; onDone: () => void }) {
  const slides = section.slides;
  const [i, setI] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [paused, setPaused] = useState(false);
  const [seen, setSeen] = useState(0);
  const [voice, setVoice] = useState(() => readVoice());
  const speaking = useRef(false);
  const duration = slideMs(slides[i]!);
  const finished = seen >= slides.length;

  // Vorlesen mit der Sprachausgabe des Geräts (kein Dienst von HandVero); die Folie bleibt, bis der Satz zu Ende ist.
  useEffect(() => {
    speaking.current = false;
    if (!voice || paused || finished || !("speechSynthesis" in window)) return;
    const u = new SpeechSynthesisUtterance(`${slides[i]!.title}. ${slides[i]!.text}`);
    u.lang = "de-DE";
    u.rate = 0.95;
    speaking.current = true;
    u.onend = u.onerror = () => {
      speaking.current = false;
    };
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
    return () => {
      speaking.current = false;
      window.speechSynthesis.cancel();
    };
  }, [i, voice, paused, finished, slides]);

  useEffect(() => {
    if (paused || finished) return;
    const t0 = performance.now() - elapsed;
    let raf = 0;
    const tick = (now: number) => {
      const e = now - t0;
      if (e >= duration && speaking.current) {
        setElapsed(duration);
        raf = requestAnimationFrame(tick);
        return;
      }
      if (e >= duration) {
        setElapsed(0);
        setSeen((s) => Math.max(s, i + 1));
        if (i < slides.length - 1) setI(i + 1);
        else setElapsed(duration);
        return;
      }
      setElapsed(e);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i, paused, finished, duration]);

  const go = (n: number) => {
    setI(n);
    setElapsed(0);
  };
  const slide = slides[i]!;
  const canForward = i < seen && i < slides.length - 1;

  return (
    <div className="trn-film">
      <div className="trn-screen" onClick={() => !finished && setPaused((p) => !p)}>
        <div className="trn-bars" aria-hidden="true">
          {slides.map((s, k) => (
            <span key={s.title}>
              <i style={{ transform: `scaleX(${k < i || (k === i && finished) ? 1 : k === i ? elapsed / duration : 0})` }} />
            </span>
          ))}
        </div>
        <span className="trn-screen-num" aria-hidden="true">
          {String(index + 1).padStart(2, "0")}
        </span>
        <div className="trn-slide has-art" key={i} aria-live="polite">
          <p className="trn-slide-k">
            {i + 1} / {slides.length}
          </p>
          <h3>{slide.title}</h3>
          <p>{slide.text}</p>
        </div>
        <VeroStage key={`s${i}`} scene={slide.scene ?? "point"} pictos={slide.pictos ?? []} />
        {paused && !finished && (
          <span className="trn-paused">
            <Icon name="stop" /> Angehalten
          </span>
        )}
      </div>
      <div className="trn-film-ctl">
        <button className="ab-btn" onClick={() => go(Math.max(0, i - 1))} disabled={i === 0} aria-label="Vorherige Folie">
          <Icon name="left" />
        </button>
        <button className="dbtn ghost" onClick={() => setPaused((p) => !p)} disabled={finished}>
          {paused ? "Weiter abspielen" : "Anhalten"}
        </button>
        <button className="ab-btn" onClick={() => go(i + 1)} disabled={!canForward} aria-label="Nächste Folie">
          <Icon name="right" />
        </button>
        {"speechSynthesis" in window && (
          <button
            className="dbtn ghost trn-voice"
            aria-pressed={voice}
            onClick={() => {
              setVoice((v) => !v);
              saveVoice(!voice);
            }}
          >
            <Icon name="mic" /> Vorlesen {voice ? "an" : "aus"}
          </button>
        )}
        <span className="trn-film-gap" />
        <button className="dbtn" onClick={onDone} disabled={!finished}>
          Zu den Fragen <Icon name="right" />
        </button>
      </div>
    </div>
  );
}

const VOICE_KEY = "hv-schulung-vorlesen";
function readVoice(): boolean {
  try {
    return localStorage.getItem(VOICE_KEY) === "1";
  } catch {
    return false;
  }
}
function saveVoice(on: boolean) {
  try {
    localStorage.setItem(VOICE_KEY, on ? "1" : "0");
  } catch {
    /* ohne Speicher bleibt die Einstellung nur für diese Sitzung */
  }
}

/** Eigenes Video: Vorspulen über die bereits gesehene Stelle hinaus wird zurückgesetzt. */
function GuardedVideo({ src, onEnded }: { src: string; onEnded: () => void }) {
  const ref = useRef<HTMLVideoElement>(null);
  const maxSeen = useRef(0);
  const [ended, setEnded] = useState(false);
  const [failed, setFailed] = useState(false);
  return (
    <div className="trn-film">
      <div className="trn-screen video">
        <video
          ref={ref}
          src={src}
          controls
          controlsList="nodownload noplaybackrate"
          disablePictureInPicture
          playsInline
          preload="metadata"
          onTimeUpdate={(e) => {
            const v = e.currentTarget;
            if (!v.seeking) maxSeen.current = Math.max(maxSeen.current, v.currentTime);
          }}
          onSeeking={(e) => {
            const v = e.currentTarget;
            if (v.currentTime > maxSeen.current + 1) v.currentTime = maxSeen.current;
          }}
          onEnded={() => setEnded(true)}
          onError={() => setFailed(true)}
        />
      </div>
      {failed && <Message>Das Video lässt sich nicht abspielen. Bitte sagen Sie der verantwortlichen Person Bescheid.</Message>}
      <div className="trn-film-ctl">
        <span className="muted-s">Bitte sehen Sie das Video bis zum Ende an.</span>
        <span className="trn-film-gap" />
        <button className="dbtn" onClick={onEnded} disabled={!ended}>
          Zu den Fragen <Icon name="right" />
        </button>
      </div>
    </div>
  );
}

/** Reihenfolge der Antworten je Durchgang mischen, damit man sich keine Positionen merkt. */
function shuffled(n: number, seed: number, salt: string): number[] {
  let h = seed * 2654435761 + 1;
  for (const ch of salt) h = Math.imul(h ^ ch.charCodeAt(0), 2246822507) >>> 0;
  const out = Array.from({ length: n }, (_, k) => k);
  for (let k = n - 1; k > 0; k--) {
    h = Math.imul(h ^ (h >>> 15), 2246822507) >>> 0;
    const j = h % (k + 1);
    [out[k], out[j]] = [out[j]!, out[k]!];
  }
  return out;
}

function Quiz({ section, seed, onSubmit }: { section: PublicSection; seed: number; onSubmit: (a: TrainingAnswers) => Promise<void> }) {
  const [answers, setAnswers] = useState<TrainingAnswers>({});
  const [sending, setSending] = useState(false);
  const orders = useMemo(() => Object.fromEntries(section.questions.map((q) => [q.id, shuffled(q.options.length, seed, q.id)])), [section, seed]);
  const complete = section.questions.every((q) => (answers[q.id] ?? []).length > 0);

  function pick(qid: string, opt: number, multiple: boolean) {
    setAnswers((a) => {
      const cur = a[qid] ?? [];
      if (!multiple) return { ...a, [qid]: [opt] };
      return { ...a, [qid]: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] };
    });
  }

  return (
    <form
      className="trn-quiz"
      onSubmit={async (e) => {
        e.preventDefault();
        setSending(true);
        await onSubmit(answers);
        setSending(false);
      }}
    >
      {section.questions.map((q, n) => (
        <fieldset key={q.id} className="trn-q">
          <legend>
            <span>{n + 1}</span>
            {q.text}
          </legend>
          {q.picto && PICTOS[q.picto] && (
            <div className="trn-qpicto">
              <Picto id={q.picto} size={92} label={false} />
              <small>Sehen Sie sich das Zeichen genau an.</small>
            </div>
          )}
          {q.multiple && <p className="trn-q-hint">Mehrere Antworten sind richtig.</p>}
          <div className="trn-opts">
            {orders[q.id]!.map((opt) => {
              const on = (answers[q.id] ?? []).includes(opt);
              return (
                <button
                  type="button"
                  key={opt}
                  className={`trn-opt${on ? " on" : ""}${q.multiple ? " multi" : ""}`}
                  role={q.multiple ? "checkbox" : "radio"}
                  aria-checked={on}
                  onClick={() => pick(q.id, opt, q.multiple)}
                >
                  <i aria-hidden="true">{on && <Icon name="check" />}</i>
                  {q.options[opt]}
                </button>
              );
            })}
          </div>
        </fieldset>
      ))}
      <div className="trn-film-ctl">
        <span className="muted-s">Alle Fragen müssen richtig sein. Sonst sehen Sie den Abschnitt noch einmal.</span>
        <span className="trn-film-gap" />
        <button className="dbtn" disabled={!complete || sending}>
          {sending ? "Wird geprüft …" : "Antworten prüfen"}
        </button>
      </div>
    </form>
  );
}

function ResultPanel({ result, section, last, onNext }: { result: AnswerResult; section: PublicSection; last: boolean; onNext: () => void }) {
  if (result.passed) {
    return (
      <div className="trn-result ok">
        <span className="trn-result-ico">
          <Icon name="check" />
        </span>
        <p className="dkicker">Abschnitt bestanden</p>
        <h2>
          {result.correct} von {result.total} richtig.
        </h2>
        <p>{last ? "Alle Abschnitte sind geschafft. Jetzt fehlt nur noch Ihre Bestätigung." : "Sehr gut. Weiter geht es mit dem nächsten Abschnitt."}</p>
        <button className="dbtn" onClick={onNext} autoFocus>
          {last ? "Zur Bestätigung" : "Nächster Abschnitt"} <Icon name="right" />
        </button>
      </div>
    );
  }
  const wrong = section.questions.filter((q) => result.wrong.includes(q.id));
  return (
    <div className="trn-result bad">
      <p className="dkicker">Noch nicht bestanden</p>
      <h2>
        {result.correct} von {result.total} richtig.
      </h2>
      <p>Diese Fragen sind noch nicht ganz richtig beantwortet:</p>
      <ul>
        {wrong.map((q) => (
          <li key={q.id}>{q.text}</li>
        ))}
      </ul>
      <p>Sehen Sie sich den Abschnitt „{section.title}“ bitte noch einmal an. Danach kommen die Fragen erneut, in anderer Reihenfolge.</p>
      <button className="dbtn" onClick={onNext} autoFocus>
        Abschnitt wiederholen
      </button>
    </div>
  );
}

function FinalStep(props: { attempt: TrainingAttempt; confirmText: string; suggestedName: string; onDone: (a: TrainingAttempt) => void }) {
  const [confirmed, setConfirmed] = useState(false);
  const [signature, setSignature] = useState<Signature | null>(null);
  const [signing, setSigning] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  async function complete() {
    if (!signature) return;
    setSending(true);
    setError("");
    try {
      props.onDone(await api.post<TrainingAttempt>(`/api/training-attempts/${props.attempt.id}/complete`, { confirm: true, signature }));
    } catch (err) {
      setError(errorText(err));
      setSending(false);
    }
  }

  return (
    <div className="trn-final">
      <header className="trn-stage-head">
        <p className="dkicker">Letzter Schritt</p>
        <h2>Bestätigen und unterschreiben</h2>
      </header>
      <p>Alle Abschnitte sind bestanden. Mit Ihrer Unterschrift bestätigen Sie die Teilnahme; danach erstellt HandVero den Nachweis als PDF.</p>
      <label className="trn-confirm">
        <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
        <span>{props.confirmText}</span>
      </label>
      <div className="trn-sign">
        {signature ? (
          <div className="prot-signed">
            <img src={signature.image} alt={`Unterschrift ${signature.name}`} />
            <span>
              <b>{signature.name}</b>
              <small>{new Date(signature.signedAt).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}</small>
            </span>
            <button type="button" className="btn-text danger" onClick={() => setSignature(null)}>
              Entfernen
            </button>
          </div>
        ) : (
          <button type="button" className="prot-signbtn" onClick={() => setSigning(true)}>
            <Icon name="pen" /> Unterschreiben
          </button>
        )}
      </div>
      <p className="muted-s">{TRAINING_ELEARNING_NOTE}</p>
      <Message>{error}</Message>
      <div className="trn-film-ctl">
        <span className="trn-film-gap" />
        <button className="dbtn" disabled={!confirmed || !signature || sending} onClick={() => void complete()}>
          {sending ? "Nachweis wird erstellt …" : "Abschließen und Nachweis erstellen"}
        </button>
      </div>
      {signing && (
        <SignatureSheet
          title="Unterschrift Teilnehmer/in"
          confirm={props.confirmText}
          initialName={props.suggestedName}
          onClose={() => setSigning(false)}
          onSigned={(s) => {
            setSigning(false);
            setSignature(s);
          }}
        />
      )}
    </div>
  );
}
